import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Check, Wifi, ArrowRight, Gauge, Activity, Shield, Zap, Plus, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  cyan: { bg: '#00F0FF', glow: 'rgba(0,240,255,0.3)', ring: 'rgba(0,240,255,0.15)' },
  gold: { bg: '#D4AF37', glow: 'rgba(212,175,55,0.3)', ring: 'rgba(212,175,55,0.15)' },
  magenta: { bg: '#FF00E5', glow: 'rgba(255,0,229,0.3)', ring: 'rgba(255,0,229,0.15)' },
  indigo: { bg: '#6366F1', glow: 'rgba(99,102,241,0.3)', ring: 'rgba(99,102,241,0.15)' },
};

const TIER_FEATURES = {
  base: [
    'Entry-level Q-Band Capacity',
    'QoS-1 Standard Execution',
    'Basic Platform Scanning',
    'Self-serve DMCA Templates',
    'Weekly Scan Frequency',
    'Community Support',
  ],
  expanded: [
    'Moderate Q-Band Capacity',
    'QoS-2 Improved Execution',
    'All Major Platforms',
    'Auto-generated DMCA',
    'Daily Scan Frequency',
    'Basic AI Editor',
    'Priority Support',
  ],
  high_performance: [
    'High Q-Band Capacity',
    'QoS-3 Priority Execution',
    'All + Dark Web Scanning',
    'Auto-sent DMCA Enforcement',
    'Real-time Scanning',
    'Full AI Editor Suite',
    'B2B Marketplace Access',
    'Priority Payouts',
  ],
  enterprise: [
    'Dedicated Q-Band Capacity',
    'QoS-MAX SLA-backed Performance',
    'Everything in High Performance',
    'Dedicated Account Manager',
    'Custom API Access',
    'White-label Options',
    'Enterprise SLA',
    'Instant Payouts',
  ],
};

const ENTITY_COLORS = {
  smithers_xi: '#00F0FF',
  quadra_core: '#D4AF37',
  creators_agent: '#FF00E5',
  creators_stake: '#6366F1',
};

const ENTITY_LABELS = {
  smithers_xi: 'SMITHERS XI',
  quadra_core: 'QUADRA CORE',
  creators_agent: "CREATOR'S AGENT",
  creators_stake: "CREATOR'S STAKE",
};

export default function SubscriptionPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [addons, setAddons] = useState([]);
  const [bursts, setBursts] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedEntity, setExpandedEntity] = useState(null);

  useEffect(() => {
    fetch(`${API}/sudds/tiers`).then(r => r.json()).then(d => {
      setTiers(d.tiers || []);
      setAddons(d.addons || []);
      setBursts(d.bursts || []);
    }).catch(() => {});
    fetch(`${API}/sudds/catalog`).then(r => r.json()).then(d => setCatalog(d.catalog || {})).catch(() => {});
    if (token) {
      fetch(`${API}/sudds/usage`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { if (d.tier) setUsage(d); }).catch(() => {});
    }
  }, [token]);

  const handleCheckout = async (sku) => {
    if (!token) { navigate('/auth'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/sudds/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sku, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleBurst = async (burstId) => {
    if (!token) { navigate('/auth'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/sudds/burst`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ burst_id: burstId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleUpgrade = async (tierId) => {
    if (!token) { navigate('/auth'); return; }
    setLoading(true);
    try {
      await fetch(`${API}/sudds/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_tier: tierId }),
      });
      const res = await fetch(`${API}/sudds/usage`, { headers: { Authorization: `Bearer ${token}` } });
      setUsage(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatBytes = (bytes) => {
    if (bytes <= 0) return '0 B';
    const gb = bytes / 1_073_741_824;
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    return `${(bytes / 1_048_576).toFixed(0)} MB`;
  };

  return (
    <div className="min-h-screen bg-texture" data-testid="subscription-page">
      {/* Hero */}
      <div className="relative overflow-hidden py-16 px-4">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center top, rgba(0,240,255,0.05) 0%, transparent 60%)' }} />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.15)' }}>
            <Wifi size={12} className="text-[#00F0FF]" />
            <span className="text-[10px] font-black" style={{ fontFamily: 'Orbitron', color: '#00F0FF', letterSpacing: '2px' }}>SUDDS / QBN4 PROTOCOL</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-3" style={{ fontFamily: 'Orbitron' }}>
            <span className="text-white">Q-Band</span>
            <span style={{ color: '#00F0FF' }}> Bandwidth</span>
          </h1>
          <p className="text-gray-400 text-sm max-w-lg mx-auto">
            Adaptive throughput powered by the QBN4 protocol. Always on, no anxiety — just smooth, optimized scaling when you need more.
          </p>
        </div>
      </div>

      {/* Usage Telemetry */}
      {usage && (
        <div className="max-w-5xl mx-auto px-4 mb-10" data-testid="qband-telemetry">
          <div className="rounded-2xl p-6" style={{
            background: 'rgba(13,13,13,0.8)',
            border: `1px solid ${TIER_COLORS[usage.tier_color]?.ring || 'rgba(0,240,255,0.1)'}`,
            boxShadow: `0 0 30px ${TIER_COLORS[usage.tier_color]?.glow || 'rgba(0,240,255,0.05)'}`,
          }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                  background: `${TIER_COLORS[usage.tier_color]?.bg || '#00F0FF'}15`,
                  border: `1px solid ${TIER_COLORS[usage.tier_color]?.bg || '#00F0FF'}30`,
                }}>
                  <Gauge size={18} style={{ color: TIER_COLORS[usage.tier_color]?.bg || '#00F0FF' }} />
                </div>
                <div>
                  <p className="text-xs font-black" style={{ fontFamily: 'Orbitron', color: TIER_COLORS[usage.tier_color]?.bg || '#00F0FF' }}>
                    {usage.tier_name}
                  </p>
                  <p className="text-[9px] text-gray-500">{usage.qos} • ${usage.price}/mo</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  usage.state === 'normal' ? 'bg-green-400' : usage.state === 'approaching' ? 'bg-amber-400 animate-pulse' : 'bg-red-500 animate-pulse'
                }`} />
                <span className={`text-[10px] font-black ${
                  usage.state === 'normal' ? 'text-green-400' : usage.state === 'approaching' ? 'text-amber-400' : 'text-red-500'
                }`} style={{ fontFamily: 'Orbitron' }}>
                  {usage.state === 'normal' ? 'FULL THROUGHPUT' : usage.state === 'approaching' ? 'APPROACHING CAPACITY' : 'OPTIMIZED MODE'}
                </span>
              </div>
            </div>
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-[10px] text-gray-400">
                  {formatBytes(usage.consumed_bytes)} used
                  {usage.limit_bytes > 0 ? ` of ${formatBytes(usage.limit_bytes)}` : ' (Dedicated Capacity)'}
                </span>
                <span className="text-[10px] font-bold" style={{ color: TIER_COLORS[usage.tier_color]?.bg || '#00F0FF' }}>
                  {usage.remainingQBand !== undefined && usage.remainingQBand !== 'Unlimited'
                    ? `${usage.remainingQBand} GB remaining`
                    : usage.limit_bytes > 0 ? `${usage.percent_used}%` : 'QoS-MAX'}
                </span>
              </div>
              <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="h-full rounded-full transition-all duration-700" style={{
                  width: `${Math.min(usage.percent_used, 100)}%`,
                  background: usage.state === 'normal'
                    ? `linear-gradient(90deg, ${TIER_COLORS[usage.tier_color]?.bg || '#00F0FF'}, ${TIER_COLORS[usage.tier_color]?.bg || '#00F0FF'}88)`
                    : usage.state === 'approaching'
                    ? 'linear-gradient(90deg, #F59E0B, #DC2626)'
                    : 'linear-gradient(90deg, #DC2626, #7F1D1D)',
                  boxShadow: `0 0 10px ${usage.state === 'normal' ? TIER_COLORS[usage.tier_color]?.glow || 'rgba(0,240,255,0.3)' : usage.state === 'approaching' ? 'rgba(245,158,11,0.4)' : 'rgba(220,38,38,0.4)'}`,
                }} />
              </div>
            </div>
            {usage.state === 'optimized' && (
              <div className="mt-3 p-3 rounded-xl flex items-center justify-between" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.15)' }}>
                <div className="flex items-center gap-2">
                  <Activity size={14} className="text-amber-400" />
                  <span className="text-xs text-amber-300">Your system is operating in optimized mode. Expand your bandwidth for full performance.</span>
                </div>
                <button
                  onClick={() => {
                    const nextTier = usage.tier === 'base' ? 'expanded' : usage.tier === 'expanded' ? 'high_performance' : 'enterprise';
                    handleUpgrade(nextTier);
                  }}
                  data-testid="expand-bandwidth-btn"
                  className="px-4 py-1.5 rounded-full text-[10px] font-black text-black whitespace-nowrap ml-3"
                  style={{ background: '#00F0FF', boxShadow: '0 0 15px rgba(0,240,255,0.3)' }}
                >
                  EXPAND BANDWIDTH
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === SMITHERS XI Q-Band Tiers === */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1.5 h-5 rounded-full" style={{ background: '#00F0FF' }} />
          <h2 className="text-sm font-black" style={{ fontFamily: 'Orbitron', color: '#00F0FF', letterSpacing: '1px' }}>SMITHERS XI — Q-BAND TIERS</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tier-grid">
          {tiers.map((tier, i) => {
            const colors = TIER_COLORS[tier.color] || TIER_COLORS.cyan;
            const features = TIER_FEATURES[tier.tier_id] || [];
            const isCurrentTier = usage?.tier === tier.tier_id;
            return (
              <div key={tier.tier_id}
                className={`rounded-2xl overflow-hidden flex flex-col transition-all hover:scale-[1.02] ${isCurrentTier ? 'ring-2' : ''}`}
                style={{ background: 'rgba(13,13,13,0.9)', border: `1px solid ${colors.ring}`, '--tw-ring-color': colors.bg,
                  boxShadow: isCurrentTier ? `0 0 30px ${colors.glow}` : 'none' }}
                data-testid={`tier-${tier.tier_id}`}>
                <div className="p-5 pb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full" style={{
                      fontFamily: 'Orbitron', background: `${colors.bg}15`, color: colors.bg, border: `1px solid ${colors.bg}30`, letterSpacing: '1px' }}>
                      TIER {i + 1}
                    </span>
                    {isCurrentTier && <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-green-500/20 text-green-400" style={{ fontFamily: 'Orbitron' }}>CURRENT</span>}
                  </div>
                  <h3 className="text-sm font-black mb-1" style={{ fontFamily: 'Orbitron', color: colors.bg }}>{tier.name}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    {tier.price > 0 ? (
                      <><span className="text-3xl font-black text-white">${tier.price}</span><span className="text-xs text-gray-500">/mo</span></>
                    ) : (
                      <span className="text-xl font-black text-white">Custom</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Wifi size={10} style={{ color: colors.bg }} />
                    <span className="text-[10px] text-gray-400">
                      {typeof tier.qband_gb === 'number' ? `${tier.qband_gb} GB` : tier.qband_gb} Q-Band
                    </span>
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full ml-1" style={{ background: `${colors.bg}10`, color: colors.bg }}>{tier.qos}</span>
                  </div>
                </div>
                <div className="mx-5 h-px" style={{ background: `${colors.bg}15` }} />
                <div className="p-5 flex-1">
                  <div className="space-y-2">
                    {features.map((feat, j) => (
                      <div key={j} className="flex items-start gap-2">
                        <Check size={11} className="mt-0.5 flex-shrink-0" style={{ color: colors.bg }} />
                        <span className="text-[11px] text-gray-400">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-5 pt-0">
                  <button onClick={() => tier.price > 0 ? handleCheckout(tier.sku) : null}
                    disabled={isCurrentTier || loading || tier.price <= 0}
                    data-testid={`select-${tier.tier_id}`}
                    className="w-full py-3 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all disabled:opacity-30"
                    style={isCurrentTier ? { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#666' }
                      : { background: `${colors.bg}15`, border: `1px solid ${colors.bg}30`, color: colors.bg }}>
                    {isCurrentTier ? 'CURRENT PLAN' : tier.price <= 0 ? 'CONTACT SALES' : <><span>UNLOCK Q-BAND</span><ArrowRight size={12} /></>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add-ons & Burst */}
      <div className="max-w-5xl mx-auto px-4 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Add-ons */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(13,13,13,0.8)', border: '1px solid rgba(0,240,255,0.08)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Plus size={12} className="text-[#00F0FF]" />
              <span className="text-[10px] font-black" style={{ fontFamily: 'Orbitron', color: '#00F0FF', letterSpacing: '1px' }}>ADD-ONS</span>
            </div>
            <div className="space-y-3">
              {addons.map(addon => (
                <div key={addon.sku} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(0,240,255,0.02)', border: '1px solid rgba(0,240,255,0.06)' }}>
                  <div>
                    <p className="text-xs font-bold text-white">{addon.name}</p>
                    <p className="text-[10px] text-gray-500">${addon.price}/mo</p>
                  </div>
                  <button onClick={() => handleCheckout(addon.sku)} disabled={loading}
                    data-testid={`addon-${addon.sku}`}
                    className="px-3 py-1.5 rounded-full text-[9px] font-black transition-all hover:opacity-80"
                    style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF', border: '1px solid rgba(0,240,255,0.2)' }}>
                    ADD
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Burst Purchases */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(13,13,13,0.8)', border: '1px solid rgba(212,175,55,0.08)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Zap size={12} className="text-[#D4AF37]" />
              <span className="text-[10px] font-black" style={{ fontFamily: 'Orbitron', color: '#D4AF37', letterSpacing: '1px' }}>BURST PURCHASES</span>
              <span className="text-[8px] text-gray-600 ml-1">One-time Q-Band top-up</span>
            </div>
            <div className="space-y-3">
              {bursts.map(burst => (
                <div key={burst.sku} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(212,175,55,0.02)', border: '1px solid rgba(212,175,55,0.06)' }}>
                  <div>
                    <p className="text-xs font-bold text-white">{burst.name}</p>
                    <p className="text-[10px] text-gray-500">${burst.price} one-time</p>
                  </div>
                  <button onClick={() => handleBurst(burst.sku.includes('5K') ? '5k' : burst.sku.includes('10K') ? '10k' : '25k')}
                    disabled={loading}
                    data-testid={`burst-${burst.sku}`}
                    className="px-3 py-1.5 rounded-full text-[9px] font-black transition-all hover:opacity-80"
                    style={{ background: 'rgba(212,175,55,0.1)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.2)' }}>
                    BUY
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* === OTHER ENTITIES === */}
      <div className="max-w-5xl mx-auto px-4 pb-12">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1.5 h-5 rounded-full" style={{ background: '#D4AF37' }} />
          <h2 className="text-sm font-black" style={{ fontFamily: 'Orbitron', color: '#D4AF37', letterSpacing: '1px' }}>ECOSYSTEM PRODUCTS</h2>
        </div>
        <div className="space-y-3">
          {Object.entries(catalog).filter(([k]) => k !== 'smithers_xi').map(([entityId, entity]) => (
            <div key={entityId} className="rounded-2xl overflow-hidden" style={{ background: 'rgba(13,13,13,0.8)', border: `1px solid ${ENTITY_COLORS[entityId]}15` }}>
              <button
                onClick={() => setExpandedEntity(expandedEntity === entityId ? null : entityId)}
                className="w-full p-5 flex items-center justify-between"
                data-testid={`entity-${entityId}`}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 rounded-full" style={{ background: ENTITY_COLORS[entityId] }} />
                  <div className="text-left">
                    <p className="text-xs font-black" style={{ fontFamily: 'Orbitron', color: ENTITY_COLORS[entityId] }}>
                      {ENTITY_LABELS[entityId]}
                    </p>
                    <p className="text-[9px] text-gray-500">{entity.entity} • {entity.products.length} products</p>
                  </div>
                </div>
                {expandedEntity === entityId ? <ChevronUp size={14} className="text-gray-600" /> : <ChevronDown size={14} className="text-gray-600" />}
              </button>
              {expandedEntity === entityId && (
                <div className="px-5 pb-5 pt-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {entity.products.map(product => (
                      <div key={product.sku} className="p-4 rounded-xl" style={{ background: `${ENTITY_COLORS[entityId]}05`, border: `1px solid ${ENTITY_COLORS[entityId]}10` }}>
                        <p className="text-xs font-bold text-white mb-1">{product.name}</p>
                        <div className="flex items-baseline gap-1 mb-3">
                          {product.price > 0 ? (
                            <>
                              <span className="text-lg font-black text-white">${product.price % 1 !== 0 ? product.price.toFixed(2) : product.price}</span>
                              {product.type === 'subscription' && <span className="text-[9px] text-gray-500">/mo</span>}
                              {product.type === 'one_time' && <span className="text-[9px] text-gray-500">one-time</span>}
                            </>
                          ) : (
                            <span className="text-sm font-black text-gray-400">Custom</span>
                          )}
                        </div>
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{
                          background: `${ENTITY_COLORS[entityId]}10`, color: ENTITY_COLORS[entityId],
                          border: `1px solid ${ENTITY_COLORS[entityId]}20` }}>
                          {product.type === 'subscription' ? 'SUBSCRIPTION' : product.type === 'one_time' ? 'ONE-TIME' : product.type.toUpperCase()}
                        </span>
                        <button onClick={() => product.price > 0 ? handleCheckout(product.sku) : null}
                          disabled={loading || product.price <= 0}
                          data-testid={`checkout-${product.sku}`}
                          className="w-full mt-3 py-2 rounded-lg text-[9px] font-black flex items-center justify-center gap-1.5 transition-all disabled:opacity-20"
                          style={{ background: `${ENTITY_COLORS[entityId]}10`, color: ENTITY_COLORS[entityId], border: `1px solid ${ENTITY_COLORS[entityId]}20` }}>
                          {product.price > 0 ? <><span>PURCHASE</span><ExternalLink size={9} /></> : 'CONTACT SALES'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* QBN4 Info */}
      <div className="max-w-5xl mx-auto px-4 pb-20">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <Shield size={14} className="text-[#00F0FF]" />
            <span className="text-[10px] font-black" style={{ fontFamily: 'Orbitron', color: '#00F0FF', letterSpacing: '1.5px' }}>QBN4 PROTOCOL — ALWAYS ON</span>
          </div>
          <p className="text-xs text-gray-500 max-w-lg mx-auto">
            When you reach your Q-Band capacity, your service continues seamlessly. The QBN4 protocol optimizes your throughput until your cycle resets or you expand your bandwidth.
          </p>
          <div className="flex items-center justify-center gap-6 mt-6">
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-green-400 mx-auto mb-1" />
              <p className="text-[9px] text-gray-500">0-80%</p>
              <p className="text-[9px] font-bold text-green-400">FULL 1.0x</p>
            </div>
            <ArrowRight size={12} className="text-gray-700" />
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-amber-400 mx-auto mb-1 animate-pulse" />
              <p className="text-[9px] text-gray-500">80-99%</p>
              <p className="text-[9px] font-bold text-amber-400">APPROACHING 0.8x</p>
            </div>
            <ArrowRight size={12} className="text-gray-700" />
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-red-500 mx-auto mb-1 animate-pulse" />
              <p className="text-[9px] text-gray-500">100%+</p>
              <p className="text-[9px] font-bold text-red-500">OPTIMIZED 0.4x</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
